import { SOUND_EVENTS } from './registry';
import { date, utc, validateEventPage } from './contract';

export function eligibleEvent(event, page) {
  const definition = SOUND_EVENTS.find((entry) => entry.id === event.event_type);
  if (!definition || !definition.detect(event) || !event.sound_candidate ||
      !event.live_sound_eligible || !event.calendar.eligible || !page.evaluator_fresh) return false;
  const server = Date.parse(page.server_time_utc);
  if (![event.occurred_at, event.published_at].every((at) =>
    utc(at) && server >= Date.parse(at) && server - Date.parse(at) <= 15000)) return false;
  const eligibleCalendar = (context, businessId, scheduled) => {
    const business = page.business_calendar.find((c) => c.business_id === businessId);
    return Boolean(business && business.reason === null && typeof business.timezone === 'string' &&
      business.timezone.length > 0 && date(business.today) &&
      context.reason === null && context.timezone === business.timezone &&
      context.today === business.today && date(scheduled) && scheduled >= business.today);
  };
  if (event.event_type === 'new_driver_message') {
    // Receipt-local day is specific to messages; never fabricate an order date.
    if (Array.isArray(event.calendar.businesses)) {
      return event.business_ids.every((id) => event.calendar.businesses.some((c) =>
        c.business_id === id && c.eligible === true && eligibleCalendar(c, id, c.message_local_date)));
    }
    return event.business_ids.length === 1 && eligibleCalendar(
      event.calendar, event.business_ids[0], event.calendar.message_local_date);
  }
  if (event.event_type === 'doordash_exception') {
    // v1 DoorDash creations need not repeat routine-specific delivery_date or
    // calendar.scheduled_date fields. calendar.eligible is the server's current
    // scheduled-date/ownership decision, not receipt-date inference.
    const business = page.business_calendar.find((c) => c.business_id === event.business_id);
    return Boolean(business && business.reason === null && date(business.today) &&
      typeof business.timezone === 'string' && business.timezone.length > 0);
  }
  return event.delivery_date === event.calendar.scheduled_date &&
    eligibleCalendar(event.calendar, event.business_id, event.calendar.scheduled_date);
}

export class EventMonitor {
  constructor({ request, scope, deliver, context = async () => true, silence = () => {},
    current = () => true, status = () => {}, now = Date.now, random = Math.random }) {
    Object.assign(this, { request, scope, deliver, context, silence, current, status, now, random });
    this.cursor = null;
    this.lastSuccess = null;
    this.failures = 0;
    this.due = 0;
    this.disposed = false;
    this.busy = false;
    this.version = 0;
  }
  async tick() {
    if (this.disposed || this.busy || !this.current() || this.now() < this.due) return;
    if (this.lastSuccess !== null && this.now() - this.lastSuccess > 15000) this.reset();
    this.busy = true;
    this.controller = new AbortController();
    const version = this.version;
    const current = () => !this.disposed && this.current() && version === this.version;
    try {
      for (let i = 0; i < 10; i += 1) {
        const baseline = !this.cursor;
        const started = this.now();
        const page = validateEventPage(await this.request({ cursor: this.cursor, signal: this.controller.signal }));
        if (!current()) return;
        if (this.now() - started > 15000) throw new Error('Event request was stale');
        if (page.mode !== (baseline ? 'baseline' : 'live') ||
            (page.has_more && page.next_cursor === this.cursor)) throw new Error('Invalid event cursor');
        if (!(await this.context(page)) || page.scope_epoch !== this.scope) {
          if (current()) this.reset();
          return;
        }
        if (!current()) return;
        if (!page.evaluator_fresh) this.silence();
        if (!baseline) {
          const received = this.now();
          for (const event of page.events) {
            if (!current()) return;
            if (eligibleEvent(event, page)) {
              // Server freshness minus conservative request elapsed time. Rechecked
              // by the delivery queue immediately before playback.
              const remaining = 15000 - Math.max(
                Date.parse(page.server_time_utc) - Date.parse(event.occurred_at),
                Date.parse(page.server_time_utc) - Date.parse(event.published_at)
              ) - (received - started);
              this.deliver({ ...event, type: event.event_type, id: String(event.id),
                expiresAt: received + Math.max(0, remaining) });
            }
          }
        }
        this.cursor = page.next_cursor;
        this.lastSuccess = this.now();
        this.failures = 0;
        this.status(page.evaluator_fresh ? 'live' : 'evaluator-stale');
        this.due = this.now() + (page.has_more ? 1 : page.poll_after_ms);
        if (!page.has_more) break;
      }
    } catch (error) {
      if (current()) {
        this.reset();
        this.status(error.status === 403 ? 'forbidden' : 'error', error);
        if (error.status === 403) this.dispose();
        else this.due = this.now() + (error.status === 409 ? 1 :
          Math.min(30000, 5000 * (2 ** Math.min(this.failures++, 3))) + this.random() * 500);
      }
    } finally { this.busy = false; }
  }
  reset() {
    this.version += 1;
    if (this.controller) this.controller.abort();
    this.cursor = null;
    this.lastSuccess = null;
    this.due = 0;
    this.silence();
  }
  dispose() { this.disposed = true; this.reset(); }
}
